import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const DataDeletion = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('dataDeletion.backToHome')}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('dataDeletion.title')}</h1>
          <p className="text-gray-600">{t('dataDeletion.subtitle')}</p>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Intro */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('dataDeletion.intro.title')}</h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('dataDeletion.intro.content')}
                </p>
              </div>
            </div>
          </div>

          {/* Silinecek Veriler */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dataDeletion.section1.title')}</h2>
            <p className="text-gray-700 mb-4">
              {t('dataDeletion.section1.content')}
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section1.accountInfo.title')}</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {t('dataDeletion.section1.accountInfo.list', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section1.socialMedia.title')}</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {t('dataDeletion.section1.socialMedia.list', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                  {t('dataDeletion.section1.socialMedia.note')}
                </p>
              </div>

              <div className="border-l-4 border-pink-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section1.content.title')}</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {t('dataDeletion.section1.content.list', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                  {t('dataDeletion.section1.content.note')}
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section1.usage.title')}</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {t('dataDeletion.section1.usage.list', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section1.payment.title')}</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {t('dataDeletion.section1.payment.list', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                  {t('dataDeletion.section1.payment.note')}
                </p>
              </div>
            </div>
          </div>

          {/* Silme Süreci */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dataDeletion.section2.title')}</h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section2.step1.title')}</h3>
                  <p className="text-gray-700">
                    {t('dataDeletion.section2.step1.content')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section2.step2.title')}</h3>
                  <p className="text-gray-700">
                    {t('dataDeletion.section2.step2.content')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section2.step3.title')}</h3>
                  <p className="text-gray-700">
                    {t('dataDeletion.section2.step3.content')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section2.step4.title')}</h3>
                  <p className="text-gray-700">
                    {t('dataDeletion.section2.step4.content')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                  ✓
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">{t('dataDeletion.section2.step5.title')}</h3>
                  <p className="text-gray-700">
                    {t('dataDeletion.section2.step5.content')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Alternatif Yöntemler */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dataDeletion.section3.title')}</h2>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">{t('dataDeletion.section3.method1.title')}</h3>
                <ol className="list-decimal list-inside text-blue-800 space-y-1">
                  {t('dataDeletion.section3.method1.steps', { returnObjects: true }).map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-2">{t('dataDeletion.section3.method2.title')}</h3>
                <p className="text-purple-800 mb-2">
                  {t('dataDeletion.section3.method2.intro')}
                </p>
                <ol className="list-decimal list-inside text-purple-800 space-y-1">
                  {t('dataDeletion.section3.method2.steps', { returnObjects: true }).map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
                <p className="text-sm text-purple-700 mt-2">
                  {t('dataDeletion.section3.method2.responseTime')}
                </p>
              </div>

              <div className="bg-pink-50 p-4 rounded-lg">
                <h3 className="font-semibold text-pink-900 mb-2">{t('dataDeletion.section3.method3.title')}</h3>
                <p className="text-pink-800 mb-2">
                  {t('dataDeletion.section3.method3.intro')}
                </p>
                <ol className="list-decimal list-inside text-pink-800 space-y-1">
                  {t('dataDeletion.section3.method3.steps', { returnObjects: true }).map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
                <p className="text-sm text-pink-700 mt-2">
                  {t('dataDeletion.section3.method3.note')}
                </p>
              </div>
            </div>
          </div>

          {/* Önemli Notlar */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">{t('dataDeletion.warnings.title')}</h3>
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  {t('dataDeletion.warnings.list', { returnObjects: true }).map((item, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Kısmi Silme Seçenekleri */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dataDeletion.section4.title')}</h2>
            <p className="text-gray-700 mb-4">
              {t('dataDeletion.section4.content')}
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800">{t('dataDeletion.section4.option1.title')}</h4>
                  <p className="text-sm text-gray-600">
                    {t('dataDeletion.section4.option1.content')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800">{t('dataDeletion.section4.option2.title')}</h4>
                  <p className="text-sm text-gray-600">
                    {t('dataDeletion.section4.option2.content')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800">{t('dataDeletion.section4.option3.title')}</h4>
                  <p className="text-sm text-gray-600">
                    {t('dataDeletion.section4.option3.content')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* İletişim */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dataDeletion.section5.title')}</h2>
            <p className="text-gray-700 mb-4">
              {t('dataDeletion.section5.content')}
            </p>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-gray-800">{t('dataDeletion.section5.dataProtection')}:</span>
                <a href="mailto:privacy@socialblast.app" className="text-blue-600 hover:underline">
                  {t('dataDeletion.section5.dataProtectionEmail')}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-gray-800">{t('dataDeletion.section5.generalSupport')}:</span>
                <a href="mailto:support@socialblast.app" className="text-blue-600 hover:underline">
                  {t('dataDeletion.section5.generalSupportEmail')}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-gray-800">{t('dataDeletion.section5.responseTime')}:</span>
                <span className="text-gray-700">{t('dataDeletion.section5.responseTimeValue')}</span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">{t('dataDeletion.section5.kvkkNotice.title')}</h3>
              <p className="text-blue-800 text-sm">
                {t('dataDeletion.section5.kvkkNotice.content')}
              </p>
            </div>
          </div>

          {/* Hızlı Silme Butonu (Sadece giriş yapmış kullanıcılar için) */}
          {currentUser && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold text-red-900 mb-2">{t('dataDeletion.quickDelete.title')}</h2>
                  <p className="text-red-800 mb-4">
                    {t('dataDeletion.quickDelete.warning')}
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition flex items-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    {t('dataDeletion.quickDelete.button')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-600">
          <p>{t('dataDeletion.footer.copyright')}</p>
          <div className="mt-2 flex justify-center gap-4 text-sm">
            <Link to="/privacy" className="hover:text-blue-600">{t('dataDeletion.footer.privacyLink')}</Link>
            <Link to="/terms" className="hover:text-blue-600">{t('dataDeletion.footer.termsLink')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
